import React, { useState, useRef, useEffect } from 'react';
import { moneyOrAbsent } from '../utils/moneyOrAbsent';
import { resolveScaleValue } from './BarChart';

/**
 * SVG Vertical Bar Chart component for time-series data.
 * Y-axis stays fixed; bars area scrolls horizontally on mobile.
 * @param {Object} props
 * @param {Array} props.data - Array of { label, value, fullLabel, isCurrent }
 * @param {string} props.valuePrefix - Prefix for values (e.g. '£')
 * @param {string} props.color - Bar color (default blue)
 * @param {number} props.height - Chart height in pixels (default 260)
 */
function VerticalBarChart({ data, valuePrefix = '', color = '#3B82F6', height = 260, currentPeriodLabel = 'Current period (incomplete)' }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const scrollRef = useRef(null);

  // Auto-scroll to the right so the most recent bars are visible first
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className="text-gray-400 text-sm text-center py-8">No data available</div>
    );
  }

  const hasCurrentPeriod = data.some(d => d.isCurrent);

  // 🚨 **SAME ROOT CAUSE AS `BarChart.js`, DIFFERENT OBSERVABLE SYMPTOM.**
  // `Math.max` returns `NaN` the instant any `d.value` is `NaN` (or coerces
  // to it), which used to poison `maxValue` for the WHOLE chart. `BarChart`
  // gates its LABEL on a percentage comparison against a NaN-poisoned scale,
  // so the visible defect there is every bar losing its label. This
  // component has no such comparison — `formatFullValue` renders
  // unconditionally inside the hover tooltip — so a poisoned `maxValue` here
  // instead breaks every bar's HEIGHT and every gridline's position (each
  // `(value / maxValue)` becomes NaN). Same fix: keep the SCALE arithmetic
  // safe (`resolveScaleValue`, imported from `BarChart.js` rather than
  // re-implemented — Code Unification Principle) while the tooltip keeps
  // reading the RAW `item.value` through `moneyOrAbsent` (`formatFullValue`
  // below), so it still shows the em dash or a loud `£NaN` rather than a
  // fabricated height or a silently blanked one.
  //
  // `, 1` is load-bearing exactly as in `BarChart.js`: if every value is
  // absent/malformed, `Math.max(0, 0, …)` would be `0` and reintroduce NaN
  // from the division side.
  const maxValue = Math.max(...data.map(d => resolveScaleValue(d.value)), 1);
  const barWidth = Math.max(20, Math.min(60, Math.floor(600 / data.length) - 8));
  const chartHeight = height;
  const bottomPadding = 60;
  const topPadding = 30;
  const drawHeight = chartHeight - bottomPadding - topPadding;

  // Y-axis dimensions
  const axisWidth = 55;

  // Bars area dimensions (no left padding — Y-axis is separate)
  const barsWidth = Math.max(data.length * (barWidth + 8) + 10, 200);

  // Abbreviated format for axis labels
  const formatValue = (val) => {
    if (valuePrefix === '\u00A3' || valuePrefix === '£') {
      if (val >= 1000000) return '\u00A3' + (val / 1000000).toFixed(1) + 'M';
      if (val >= 1000) return '\u00A3' + (val / 1000).toFixed(1) + 'k';
      return '\u00A3' + Number(val).toFixed(0);
    }
    return valuePrefix + val;
  };

  // Full format for tooltips
  //
  // 🚨 **AN ABSENT VALUE IS NOT A ZERO.** ⚖️ Owner ruling 2026-09-21: *"Guard
  // the absence marker AT THE CALL SITES. Leave `formatCurrency` a dumb
  // converter."* The three states — absent / malformed / the money, and why a
  // real `0` is never an absence — are defined once in `utils/moneyOrAbsent.js`.
  //
  // 🔻 **UNLIKE `BarChart`, NO LIVE CALLER CAN REACH THIS TODAY, and the guard
  // is still correct.** Every current `valuePrefix={'£'}` caller feeds this
  // from `getSpendOverTimeData`, which normalises with `Number(x) || 0`. But
  // `data` is a PUBLIC prop of a shared component whose contract
  // (`{ label, value, color }`) promises nothing about finiteness, so "cannot
  // be absent" is not PROVABLE here — and leaving the sibling of a guarded
  // money formatter unguarded reads as an oversight rather than a decision.
  const formatFullValue = (val) => {
    if (valuePrefix === '\u00A3' || valuePrefix === '£') {
      return moneyOrAbsent(val);
    }
    return valuePrefix + val;
  };

  // Y-axis gridlines
  const gridCount = 4;
  const gridLines = [];
  for (let i = 0; i <= gridCount; i++) {
    const val = (maxValue / gridCount) * i;
    const y = topPadding + drawHeight - (val / maxValue) * drawHeight;
    gridLines.push({ val, y });
  }

  return (
    <div className="w-full vertical-bar-chart-container" data-testid="vertical-bar-chart">
      <div className="flex">
        {/* Fixed Y-axis */}
        <svg
          width={axisWidth}
          height={chartHeight}
          viewBox={`0 0 ${axisWidth} ${chartHeight}`}
          style={{ display: 'block', flexShrink: 0 }}
        >
          {gridLines.map((g, i) => (
            <g key={i}>
              {/* Short tick mark at right edge */}
              <line
                x1={axisWidth - 5}
                y1={g.y}
                x2={axisWidth}
                y2={g.y}
                stroke="#E5E7EB"
                strokeWidth="1"
              />
              <text
                x={axisWidth - 8}
                y={g.y + 4}
                textAnchor="end"
                className="text-xs"
                fill="#9CA3AF"
                fontSize="11"
              >
                {formatValue(g.val)}
              </text>
            </g>
          ))}
          {/* Bottom axis stub */}
          <line
            x1={axisWidth - 5}
            y1={topPadding + drawHeight}
            x2={axisWidth}
            y2={topPadding + drawHeight}
            stroke="#D1D5DB"
            strokeWidth="1"
          />
        </svg>

        {/* Scrollable bars area */}
        <div ref={scrollRef} className="overflow-x-auto flex-1">
          <svg
            width={barsWidth}
            height={chartHeight}
            viewBox={`0 0 ${barsWidth} ${chartHeight}`}
            style={{ display: 'block' }}
          >
            {/* Gridlines spanning bars area */}
            {gridLines.map((g, i) => (
              <line
                key={i}
                x1={0}
                y1={g.y}
                x2={barsWidth}
                y2={g.y}
                stroke="#E5E7EB"
                strokeWidth="1"
                strokeDasharray={i === 0 ? 'none' : '4,4'}
              />
            ))}

            {/* Bars */}
            {data.map((item, i) => {
              const barH = (resolveScaleValue(item.value) / maxValue) * drawHeight;
              const x = i * (barWidth + 8) + 4;
              const y = topPadding + drawHeight - barH;
              const isHovered = hoveredIndex === i;
              const isCurrent = item.isCurrent;

              return (
                <g
                  key={i}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  className="cursor-default"
                >
                  <rect
                    x={x}
                    y={y}
                    width={barWidth}
                    height={Math.max(barH, 1)}
                    fill={isCurrent ? color : color}
                    opacity={isCurrent ? 0.45 : (hoveredIndex !== null && !isHovered ? 0.5 : 1)}
                    rx="3"
                    ry="3"
                    data-testid={isCurrent ? 'current-period-bar' : undefined}
                  />

                  {/* Hover tooltip */}
                  {isHovered && (
                    <g>
                      <rect
                        x={x + barWidth / 2 - 45}
                        y={y - 28}
                        width="90"
                        height="22"
                        fill="#1F2937"
                        rx="4"
                        ry="4"
                        opacity="0.9"
                      />
                      <text
                        x={x + barWidth / 2}
                        y={y - 14}
                        textAnchor="middle"
                        fill="white"
                        fontSize="11"
                        fontWeight="600"
                      >
                        {formatFullValue(item.value)}
                      </text>
                    </g>
                  )}

                  {/* X-axis label */}
                  <text
                    x={x + barWidth / 2}
                    y={topPadding + drawHeight + 16}
                    textAnchor="middle"
                    fill="#6B7280"
                    fontSize="10"
                    transform={data.length > 12 ? `rotate(-45, ${x + barWidth / 2}, ${topPadding + drawHeight + 16})` : ''}
                  >
                    {item.label}
                  </text>
                </g>
              );
            })}

            {/* Bottom axis line */}
            <line
              x1={0}
              y1={topPadding + drawHeight}
              x2={barsWidth}
              y2={topPadding + drawHeight}
              stroke="#D1D5DB"
              strokeWidth="1"
            />
          </svg>
        </div>
      </div>

      {/* Legend (fixed below chart) */}
      {hasCurrentPeriod && (
        <div className="flex items-center gap-3 mt-2 ml-14 text-xs text-gray-500" data-testid="current-period-legend">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: color, opacity: 1 }}></span>
            Completed
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: color, opacity: 0.45 }}></span>
            {currentPeriodLabel}
          </span>
        </div>
      )}
    </div>
  );
}

export default VerticalBarChart;
